import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Eye, Pencil, Search, Trash2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import Can from '../../components/Common/Can';
import { P } from '../../constants/permissions';
import { useApiQuery } from '../../hooks/useApi';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import FormModalLayout from '../../components/Common/FormModalLayout';
import EntityAutocomplete from '../../components/Common/EntityAutocomplete';
import { getAppHomePath } from '../../constants/navigation';
import {
  licenseService,
  identityService,
  institutionService,
} from '../../services/api';
import { unwrapList } from '../../utils/entityIds';
import { fetchAllPages } from '../../utils/apiPagination';
import {
  COND_SITUACAO_OPTIONS,
  CondicionanteForm,
  condicionanteFromApi,
  useCondicionanteServidorOptions,
} from '../../components/Licenses/CondicionantesEditor';
import FileViewerModal from '../../components/Licenses/FileViewerModal';
import LicenseDetailModal from '../../components/Licenses/LicenseDetailModal';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';
import { userFacingError } from '../../i18n/userFacingError';
import SlaBadge from '../../components/Common/SlaBadge';

const SITUACAO_LABELS = Object.fromEntries(COND_SITUACAO_OPTIONS.map((o) => [o.value, o.label]));

function formatDate(v) {
  if (!v) return '—';
  return String(v).slice(0, 10);
}

const Condicionantes = () => {
  const { t } = useTranslation('licenses');
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const [urlParams, setUrlParams] = useSearchParams();

  const [filters, setFilters] = useState({
    requerente_id: '',
    empreendimento_id: '',
    numero_licenca: '',
    orgao_licenciador_id: '',
    situacao: '',
    q: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [detailLicencaId, setDetailLicencaId] = useState(null);
  const [detailMode, setDetailMode] = useState('view');
  const [viewerAnexo, setViewerAnexo] = useState(null);
  const [anexosModal, setAnexosModal] = useState({ open: false, list: [], condicionanteId: null });
  const [reviewingId, setReviewingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const servidorOptions = useCondicionanteServidorOptions(!!editing);

  const { data: clientsPayload } = useApiQuery('identity-clients-cond-page', () =>
    identityService.getClients({ limit: 1000 }).then((r) => r.data)
  );
  const { data: enterprisesPayload } = useApiQuery('identity-enterprises-cond-page', () =>
    identityService.getEnterprises({ limit: 1000 }).then((r) => r.data)
  );
  const { data: orgaosPayload } = useApiQuery('institution-orgaos-cond-page', () =>
    institutionService.getOrgaos({ limit: 500 }).then((r) => r.data)
  );

  const clients = unwrapList(clientsPayload);
  const enterprises = unwrapList(enterprisesPayload);
  const orgaos = unwrapList(orgaosPayload);

  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        id: c.id,
        label: `${c.name || '—'}${c.document || c.cnpj ? ` · ${c.document || c.cnpj}` : ''}`,
      })),
    [clients]
  );
  const enterpriseOptions = useMemo(
    () => enterprises.map((e) => ({ id: e.id, label: e.name || e.nome || e.id })),
    [enterprises]
  );
  const orgaoOptions = useMemo(
    () => orgaos.map((o) => ({ id: o.id, label: o.name || o.nome || o.id })),
    [orgaos]
  );

  const lookupClient = useMemo(() => {
    const m = {};
    clients.forEach((c) => {
      m[c.id] = c.name || '—';
    });
    return m;
  }, [clients]);
  const lookupEnterprise = useMemo(() => {
    const m = {};
    enterprises.forEach((e) => {
      m[e.id] = e.name || e.nome || '—';
    });
    return m;
  }, [enterprises]);
  const lookupOrgao = useMemo(() => {
    const m = {};
    orgaos.forEach((o) => {
      m[o.id] = o.name || o.nome || '—';
    });
    return m;
  }, [orgaos]);

  const searchParams = useMemo(() => {
    const p = {};
    if (appliedFilters.requerente_id) p.requerente_id = appliedFilters.requerente_id;
    if (appliedFilters.empreendimento_id) p.empreendimento_id = appliedFilters.empreendimento_id;
    if (appliedFilters.numero_licenca?.trim()) p.numero_licenca = appliedFilters.numero_licenca.trim();
    if (appliedFilters.orgao_licenciador_id) p.orgao_licenciador_id = appliedFilters.orgao_licenciador_id;
    if (appliedFilters.situacao) p.situacao = appliedFilters.situacao;
    if (appliedFilters.q?.trim()) p.q = appliedFilters.q.trim();
    return p;
  }, [appliedFilters]);

  const { data: rowsRaw, isLoading, error, refetch } = useApiQuery(
    ['condicionantes-search', searchParams],
    async () => {
      return fetchAllPages((params) =>
        licenseService.searchCondicionantes({ ...searchParams, ...params })
      );
    }
  );
  const rows = Array.isArray(rowsRaw) ? rowsRaw : [];

  useEffect(() => {
    const highlightId = (urlParams.get('highlight') || '').trim();
    const licencaId = (urlParams.get('licenca_id') || '').trim();
    if (!highlightId && !licencaId) return;
    if (isLoading) return;
    if (licencaId) {
      setDetailLicencaId(licencaId);
      setDetailMode('view');
    } else {
      const row = rows.find((item) => String(item.id) === highlightId);
      if (row?.licenca_id) {
        setDetailLicencaId(row.licenca_id);
        setDetailMode('view');
      } else if (row) {
        setEditing({
          ...condicionanteFromApi(row),
          licenca_id: row.licenca_id,
          licenca_numero: row.licenca_numero,
        });
      }
    }
    setUrlParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('highlight');
        next.delete('licenca_id');
        return next;
      },
      { replace: true }
    );
  }, [urlParams, setUrlParams, isLoading, rows]);

  const handleApplyFilters = (e) => {
    e?.preventDefault?.();
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    const empty = {
      requerente_id: '',
      empreendimento_id: '',
      numero_licenca: '',
      orgao_licenciador_id: '',
      situacao: '',
      q: '',
    };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const todayIso = () => new Date().toISOString().slice(0, 10);

  const openEdit = (row) => {
    setEditing({
      ...condicionanteFromApi(row),
      licenca_id: row.licenca_id,
      licenca_numero: row.licenca_numero,
    });
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    if (!String(editing.condicionante || '').trim()) {
      toast.error(t('conditionTextRequired', { defaultValue: 'Informe o texto da condicionante.' }));
      return;
    }
    setSavingEdit(true);
    try {
      await licenseService.updateCondicionante(editing.id, {
        numero: Number(editing.numero) || 1,
        condicionante: editing.condicionante.trim(),
        situacao: editing.situacao || '2',
        validade: editing.validade || null,
        data_atendimento: editing.data_atendimento || null,
        informacao: editing.informacao?.trim() || null,
        responsavel_id: editing.responsavel_id || null,
      });
      toast.success('Condicionante atualizada.');
      setEditing(null);
      refetch();
    } catch (e) {
      toast.error(userFacingError(e, 'saveError'));
    } finally {
      setSavingEdit(false);
    }
  };

  const removeCondicionante = async (row) => {
    if (
      !window.confirm(
        `Remover a condicionante nº ${row.numero || ''} desta licença? O vínculo com a licença será desfeito.`
      )
    ) {
      return;
    }
    setRemovingId(row.id);
    try {
      await licenseService.deleteCondicionante(row.id);
      toast.success('Condicionante removida.');
      if (editing?.id === row.id) setEditing(null);
      refetch();
    } catch (e) {
      toast.error(userFacingError(e, 'deleteError'));
    } finally {
      setRemovingId(null);
    }
  };

  const reviewCondicionante = async (row, situacao) => {
    setReviewingId(row.id);
    try {
      await licenseService.updateCondicionante(row.id, {
        situacao,
        data_atendimento: situacao === '4' || situacao === '5' ? todayIso() : null,
      });
      toast.success(
        situacao === '4' ? 'Cumprimento aprovado.' : situacao === '5' ? 'Condicionante dispensada.' : 'Devolvida ao requerente.'
      );
      refetch();
    } catch (e) {
      toast.error(userFacingError(e, 'saveError'));
    } finally {
      setReviewingId(null);
    }
  };

  const openAnexos = async (condicionanteId) => {
    try {
      const r = await licenseService.listCondicionanteAnexos(condicionanteId);
      const d = r.data;
      const list = Array.isArray(d) ? d : d?.data ?? [];
      if (!list.length) {
        toast.error(t('noAttachments'));
        return;
      }
      setAnexosModal({ open: true, list, condicionanteId });
    } catch (e) {
      toast.error(userFacingError(e, 'licenses:attachmentsError'));
    }
  };

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("conditionsTitle")}
          description="Consulta e gestão das condicionantes já vinculadas às licenças. O cadastro novo é feito na licença; aqui você edita, remove e revisa o cumprimento."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Licenças', to: '/licenses' },
            { label: 'Condicionantes' },
          ]}
        />

        <form
          onSubmit={handleApplyFilters}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-800">
            <Search className="h-4 w-4 text-primary-600" />
            Filtros
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <EntityAutocomplete
              id="cond-f-empresa"
              label={party.consultoria ? 'Empresa / cliente' : 'Empresa / requerente'}
              value={filters.requerente_id}
              onChange={(id) => setFilters((f) => ({ ...f, requerente_id: id }))}
              options={clientOptions}
              placeholder={`Buscar ${party.one}…`}
            />
            <EntityAutocomplete
              id="cond-f-empreendimento"
              label="Empreendimento"
              value={filters.empreendimento_id}
              onChange={(id) => setFilters((f) => ({ ...f, empreendimento_id: id }))}
              options={enterpriseOptions}
              placeholder="Buscar empreendimento…"
            />
            <EntityAutocomplete
              id="cond-f-orgao"
              label="Órgão licenciador"
              value={filters.orgao_licenciador_id}
              onChange={(id) => setFilters((f) => ({ ...f, orgao_licenciador_id: id }))}
              options={orgaoOptions}
              placeholder="Buscar órgão…"
            />
            <div className="form-group">
              <label className="form-label" htmlFor="cond-f-num">
                Número da licença
              </label>
              <input
                id="cond-f-num"
                className="form-input"
                value={filters.numero_licenca}
                onChange={(e) => setFilters((f) => ({ ...f, numero_licenca: e.target.value }))}
                placeholder="Ex: 2024/001"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cond-f-sit">
                Situação
              </label>
              <select
                id="cond-f-sit"
                className="form-select"
                value={filters.situacao}
                onChange={(e) => setFilters((f) => ({ ...f, situacao: e.target.value }))}
              >
                <option value="">Todas</option>
                {COND_SITUACAO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cond-f-q">
                Texto da condicionante
              </label>
              <input
                id="cond-f-q"
                className="form-input"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Buscar no texto…"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="submit" className="btn btn-primary btn-sm">
              Aplicar filtros
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={handleClearFilters}>
              Limpar
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                const next = { ...filters, situacao: '3' };
                setFilters(next);
                setAppliedFilters(next);
              }}
            >
              Fila em atendimento
            </button>
          </div>
        </form>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {t('common:loadError')}
          </div>
        ) : null}

        <p className="text-sm text-gray-600">
          Novas condicionantes entram pelo cadastro da{' '}
          <Link to="/licenses" className="font-medium text-primary-700 hover:underline">
            licença
          </Link>
          , para manter o vínculo. Nesta listagem você edita o texto, prazos e situação, ou remove o item.
        </p>

        <div className="data-list-card overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
            <ClipboardList className="h-5 w-5 text-primary-600" />
            <span className="text-sm font-medium text-gray-800">
              {isLoading ? 'Carregando…' : `${rows.length} condicionante(s)`}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Nº</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Condicionante</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Situação</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Licença</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Empresa</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Empreendimento</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Órgão</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Validade</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Atendimento</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Anexos</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{c.numero}</td>
                    <td className="max-w-xs px-3 py-2 text-gray-900">
                      <span className="line-clamp-2">{c.condicionante}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      {SITUACAO_LABELS[String(c.situacao)] || c.situacao}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-primary-800">
                      {c.licenca_numero || '—'}
                    </td>
                    <td className="max-w-[8rem] truncate px-3 py-2 text-gray-600">
                      {c.requerente_id ? lookupClient[c.requerente_id] || `${c.requerente_id.slice(0, 8)}…` : '—'}
                    </td>
                    <td className="max-w-[8rem] truncate px-3 py-2 text-gray-600">
                      {c.empreendimento_id
                        ? lookupEnterprise[c.empreendimento_id] || `${c.empreendimento_id.slice(0, 8)}…`
                        : '—'}
                    </td>
                    <td className="max-w-[8rem] truncate px-3 py-2 text-gray-600">
                      {c.orgao_licenciador_id
                        ? lookupOrgao[c.orgao_licenciador_id] || `${c.orgao_licenciador_id.slice(0, 8)}…`
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      {c.validade && ['1', '2', '3', 1, 2, 3].includes(c.situacao) ? (
                        <SlaBadge prazo={c.validade} />
                      ) : (
                        formatDate(c.validade)
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{formatDate(c.data_atendimento)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{c.anexos_count ?? 0}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <Can permission={P.LICENSE_LICENCAS}>
                          <button
                            type="button"
                            className="btn btn-outline btn-xs"
                            title="Editar"
                            onClick={() => openEdit(c)}
                          >
                            <Pencil className="h-3 w-3" />
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-xs text-red-700"
                            title="Excluir"
                            disabled={removingId === c.id}
                            onClick={() => removeCondicionante(c)}
                          >
                            <Trash2 className="h-3 w-3" />
                            Remover
                          </button>
                        </Can>
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => {
                            setDetailLicencaId(c.licenca_id);
                            setDetailMode('view');
                          }}
                        >
                          Licença
                        </button>
                        {(c.anexos_count ?? 0) > 0 ? (
                          <button type="button" className="btn btn-outline btn-xs" onClick={() => openAnexos(c.id)}>
                            <Eye className="h-3 w-3" />
                          </button>
                        ) : null}
                        <Can permission={P.LICENSE_LICENCAS}>
                          {['3', 3].includes(c.situacao) ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                disabled={reviewingId === c.id}
                                onClick={() => reviewCondicionante(c, '4')}
                              >
                                Aprovar
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline btn-xs"
                                disabled={reviewingId === c.id}
                                onClick={() => reviewCondicionante(c, '2')}
                              >
                                Devolver
                              </button>
                            </>
                          ) : null}
                          {['1', '2', '3', 1, 2, 3].includes(c.situacao) ? (
                            <button
                              type="button"
                              className="btn btn-outline btn-xs"
                              disabled={reviewingId === c.id}
                              onClick={() => reviewCondicionante(c, '5')}
                            >
                              Dispensar
                            </button>
                          ) : null}
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                      Nenhuma condicionante encontrada com os filtros aplicados.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing ? (
        <FormModalLayout
          title={`Editar condicionante nº ${editing.numero || ''}`}
          subtitle={
            editing.licenca_numero
              ? `Vinculada à licença ${editing.licenca_numero}. O vínculo permanece ao salvar.`
              : 'O vínculo com a licença permanece ao salvar.'
          }
          headerIcon={ClipboardList}
          size="2xl"
          historyEntityType="license_conditions"
          historyEntityId={editing?.id}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>
                Cancelar
              </button>
              <Can permission={P.LICENSE_LICENCAS}>
                <button type="button" className="btn btn-primary" disabled={savingEdit} onClick={saveEdit}>
                  {savingEdit ? 'Salvando…' : 'Salvar'}
                </button>
              </Can>
            </>
          }
        >
          <CondicionanteForm
            item={editing}
            servidorOptions={servidorOptions}
            onChange={setEditing}
          />
        </FormModalLayout>
      ) : null}

      {detailLicencaId ? (
        <LicenseDetailModal
          licencaId={detailLicencaId}
          mode={detailMode}
          onClose={() => {
            setDetailLicencaId(null);
            refetch();
          }}
        />
      ) : null}

      {anexosModal.open ? (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[80vh] w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">Anexos da condicionante</h3>
            </div>
            <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
              {anexosModal.list.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm">
                  <span className="min-w-0 truncate">{a.filename_original}</span>
                  <button type="button" className="btn btn-outline btn-xs shrink-0" onClick={() => setViewerAnexo(a)}>
                    Ver
                  </button>
                </li>
              ))}
            </ul>
            <div className="border-t border-gray-200 px-4 py-3 text-right">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setAnexosModal({ open: false, list: [], condicionanteId: null })}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {viewerAnexo ? <FileViewerModal anexo={viewerAnexo} onClose={() => setViewerAnexo(null)} /> : null}
    </FormPageShell>
  );
};

export default Condicionantes;
